import { httpPost, httpGet, ServerType } from "@/service/httpRequest";

export interface StoryboardItem {
  id: number;
  timeRange: string;
  visualDesc: string;
  shotDesc: string;
  shots?: string[];
  voiceover: string;
  actionDesc: string; // 执行动作，来自详细方案
  _segmentTitle?: string;
  segmentTitle?: string;
}

export interface GenerateScriptParams {
  sessionId?: string;
  /** prompt-api 脚本分类：commerce / ad */
  scriptCategory?: 'commerce' | 'ad';
  /** prompt-api 脚本风格英文 key */
  scriptStyle?: string;
  videoType?: string;
  duration?: string;
  ratio?: string;
  productName?: string;
  targetAudience?: string;
  productBrief?: string;
  /** 当前自主创作任务选择的用户脚本 Skill ID。 */
  scriptUserSkillId?: string;
  planJson: string;
  /** 本次扣费梦想豆，对应「自主创作-生成脚本」 */
  powerCost?: number;
}

export interface RegenerateFieldResult {
  content: string;
  editCount: number;
  charged: boolean;
  powerCost: number;
}

export interface PowerConsumeRule {
  bizType: number;
  /** 规则名称，与 consume-rules 接口及扣费链路一致 */
  name: string;
  formula?: Array<{ type?: string; op?: string; value?: number | string }>;
  status?: number;
}

export interface AiCreationTaskResult<T = any> {
  taskId: string;
  taskType?: string;
  sessionId?: string;
  revisionId?: number;
  resourceId?: string;
  /** 视频任务对应的前端生成卡片标识。 */
  clipId?: string;
  createTime?: string;
  status: string;
  phase?: string;
  progress?: number;
  result?: T;
  errorCode?: string;
  errorMessage?: string;
}

/** 脚本元素提取任务返回结果。 */
export interface ElementExtractionTaskResult {
  /** 已识别的人物、产品和场景。 */
  aiCreationElements?: Record<string, Array<{ name: string; aliases?: string[] }>>;
  /** 提取状态：完整成功、部分成功或失败。 */
  elementStatus?: "SUCCESS" | "PARTIAL_SUCCESS" | "FAILED";
  /** 面向用户的提取结果说明。 */
  elementMessage?: string;
  /** 实际处理的脚本分段数。 */
  totalChunks?: number;
  /** 成功处理的脚本分段数。 */
  succeededChunks?: number;
  /** 失败处理的脚本分段数。 */
  failedChunks?: number;
}

/** AI 对话编辑脚本任务结果。 */
export interface ScriptEditTaskResult {
  /** 新脚本修订版本 ID；框架拒绝或未变更时可能为空。 */
  revisionId?: number;
  /** 修改后的完整脚本。 */
  script: string;
  /** 兼容旧结果的元素字段；文案-only 修改时通常不返回。 */
  aiCreationElements?: Record<string, Array<{ name: string; aliases?: string[] }>>;
  /** 是否因框架/名称/绑定诉求被拒绝。 */
  frameworkRejected?: boolean;
  /** 是否实际修改了脚本文案。 */
  changed?: boolean;
  /** 用户可读提示。 */
  message?: string;
}

/** 查询统一异步任务状态，供页面在 WebSocket 丢事件时兜底恢复终态。 */
export const getAiCreationTask = <T = any>(taskId: string) =>
  httpGet<AiCreationTaskResult<T>>(ServerType.Base, {
    url: `/ai-video/ai-creation/tasks/${taskId}`,
    config: { silent: true } as any,
  });

/** 查询会话任务，用于刷新后本地 taskId 缺失时恢复后台任务。 */
export const getAiCreationTasksBySession = <T = any>(sessionId: string) =>
  httpGet<AiCreationTaskResult<T>[]>(ServerType.Base, {
    url: `/ai-video/ai-creation/tasks/session/${sessionId}`,
    config: { silent: true } as any,
  });

/** 按客户端幂等键精确恢复一次提交，避免误关联同片段的其他任务。 */
export const getAiCreationTaskByIdempotency = <T = unknown>(taskType: string, idempotencyKey: string) =>
  httpGet<AiCreationTaskResult<T>>(ServerType.Base, {
    url: "/ai-video/ai-creation/tasks/by-idempotency",
    params: { taskType, idempotencyKey },
    config: { silent: true } as any,
  });

/** 后端重启后重新登记并精确接管当前用户的 AI 脚本修改任务。 */
export const resumeScriptEditTask = (taskId: string) =>
  httpPost<boolean>(ServerType.Base, {
    url: `/ai-video/ai-creation/tasks/${taskId}/resume-script-edit`,
    config: { silent: true } as any,
  });

const waitForAiCreationTask = async <T>(taskId: string, timeoutMs = 20 * 60 * 1000, signal?: AbortSignal): Promise<T> => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (signal?.aborted) throw new DOMException("AI 任务等待已取消", "AbortError");
    const task = await getAiCreationTask<T>(taskId);
    if (task.status === "SUCCEEDED") return task.result as T;
    if (["FAILED", "TIMED_OUT", "CANCELLED"].includes(task.status)) {
      throw new Error(task.errorMessage || "AI 任务执行失败");
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1200));
  }
  throw new Error("AI 任务执行超时，请稍后查看任务结果");
};

/** 异步生成分镜脚本（后台执行，立即返回，通过 WebSocket 或轮询获取结果） */
export const generateStoryboardScriptAsync = (params: GenerateScriptParams) =>
  httpPost<{ sessionId: string; taskId: string; scriptGenerateStatus: string; elementExtractStatus: string }>(ServerType.Base, {
    url: "/ai-video/storyboard/generate-script-async",
    data: params,
    config: { silent: true } as any,
  });

/** 重新生成单帧图 */
export const regenerateFrame = (
  taskId: string,
  sessionId: string,
  storyboardId: number,
  frameType: string,
  frameDesc: string,
  powerCost: number,
  userPrompt?: string,
  digitalHumanId?: string,
  productViewUrls?: string[],
  productViewSourceIds?: number[],
  canvasUserSkillId?: string,
) =>
  httpPost<{ taskId: string; status: string }>(ServerType.Base, {
    url: "/ai-video/storyboard/regenerate-frame",
    data: {
      taskId,
      sessionId,
      storyboardId,
      frameType,
      frameDesc,
      userPrompt,
      digitalHumanId,
      productViewUrls,
      productViewSourceIds,
      canvasUserSkillId,
      powerCost,
    },
    config: { silent: true } as any,
  });

/**
 * AI 重新生成单个字段（data 发 POST body，避免 URL 过长）
 * @param powerCost 应扣梦想豆；免费次数内传 0，超出免费额度后传 directorPowerCost
 */
export const regenerateField = async (
  sessionId: string,
  storyboardId: number,
  fieldType: string,
  fieldLabel: string,
  currentContent: string,
  fullScriptContext: string,
  powerCost: number,
  userHint?: string,
) => {
  const submitted = await httpPost<{ taskId: string; editCount: number }>(ServerType.Base, {
    url: "/ai-video/storyboard/regenerate-field",
    data: { sessionId, storyboardId, fieldType, fieldLabel, currentContent, fullScriptContext, userHint, powerCost },
  });
  const result = await waitForAiCreationTask<RegenerateFieldResult>(submitted.taskId);
  return { ...result, editCount: result.editCount ?? submitted.editCount };
};

export const getPowerConsumeRules = () =>
  httpGet<PowerConsumeRule[]>(ServerType.Base, {
    url: "/ai-video/power/consume-rules",
  });

/** AI 对话编辑请求参数（与 Step1 脚本生成共用 scriptCategory/scriptStyle）。 */
export interface ChatEditScriptParams {
  script: string;
  baseScript?: string;
  userPrompt: string;
  sessionId?: string;
  /** prompt-api 脚本分类：commerce / ad */
  scriptCategory?: 'commerce' | 'ad';
  /** prompt-api 脚本风格英文 key */
  scriptStyle?: string;
}

/** AI 对话编辑：根据用户提示词修改完整脚本（返回增强后的脚本+元素提取结果） */
export const submitChatEditScript = (params: ChatEditScriptParams) =>
  httpPost<{ taskId: string }>(ServerType.Base, {
    url: "/ai-video/storyboard/chat-edit-async",
    data: params,
    config: { silent: true } as any,
  });

/** 等待 AI 对话编辑任务结束，可用于页面刷新后根据 taskId 恢复任务。 */
export const waitForChatEditTask = (taskId: string, signal?: AbortSignal) =>
  waitForAiCreationTask<ScriptEditTaskResult>(taskId, 20 * 60 * 1000, signal);

/** AI 对话编辑兼容入口。 */
export const chatEditScript = async (params: ChatEditScriptParams) => {
  const submitted = await submitChatEditScript(params);
  return waitForChatEditTask(submitted.taskId);
};
